import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeft, Users, MessageCircle, Bell, TrendingUp, Send, Loader, Heart, RefreshCw, Calendar, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function AdminPanel() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [notifStatus, setNotifStatus] = useState(null);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [notifFilter, setNotifFilter] = useState('all'); // 'all' | 'email' | 'inbox'

  useEffect(() => { fetchAnalytics(); }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics`);
      setAnalytics(res.data);
    } catch (err) {
      toast.error('Access denied or failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastMessage.trim()) { toast.error('Message required'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/broadcast`, {
        subject: broadcastSubject || 'Mind My Mojo Update',
        message: broadcastMessage
      });
      const r = res.data;
      if (r.sent > 0) {
        toast.success(`Broadcast sent: ${r.sent} emails, ${r.inbox_delivered} inbox notifications`);
      } else if (r.inbox_delivered > 0) {
        toast.success(`In-app notifications delivered to ${r.inbox_delivered} users. Email delivery failed (check Resend API key).`);
      } else {
        toast.error('Broadcast failed');
      }
      setBroadcastMessage('');
      setBroadcastSubject('');
    } catch (err) {
      toast.error('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, sub, color = 'hunter-green' }) => (
    <div className="bg-white rounded-xl p-4 border border-stone-200 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-warm-charcoal/50 uppercase tracking-wider font-semibold">{label}</span>
        <Icon className={`w-4 h-4 text-${color}`} />
      </div>
      <p className="text-2xl font-bold text-warm-charcoal">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {sub && <p className="text-[10px] text-warm-charcoal/40 mt-1">{sub}</p>}
    </div>
  );

  const BarChart = ({ data, labelKey, valueKey, title, maxBars = 15 }) => {
    if (!data || data.length === 0) return null;
    const items = data.slice(0, maxBars);
    const maxVal = Math.max(...items.map(d => d[valueKey] || 0));
    return (
      <div className="bg-white rounded-xl p-4 border border-stone-200">
        <h3 className="text-xs font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">{title}</h3>
        <div className="space-y-1.5">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[10px] text-warm-charcoal/60 w-28 truncate text-right">{item[labelKey]}</span>
              <div className="flex-1 h-5 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-hunter-green/70 rounded-full transition-all"
                  style={{ width: `${Math.max(3, (item[valueKey] / maxVal) * 100)}%` }}
                />
              </div>
              <span className="text-[10px] font-medium text-warm-charcoal w-8 text-right">{item[valueKey]}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const DailyChart = ({ data, title }) => {
    if (!data || Object.keys(data).length === 0) return null;
    const entries = Object.entries(data).sort();
    const maxVal = Math.max(...entries.map(e => e[1]));
    return (
      <div className="bg-white rounded-xl p-4 border border-stone-200">
        <h3 className="text-xs font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">{title}</h3>
        <div className="flex items-end gap-1 overflow-x-auto pb-8" style={{ height: '180px' }}>
          {entries.map(([day, count]) => {
            const h = Math.max(8, (count / Math.max(maxVal, 1)) * 130);
            return (
              <div key={day} className="flex-1 min-w-[28px] flex flex-col items-center justify-end" title={`${day}: ${count}`}>
                <span className="text-[10px] font-bold text-hunter-green mb-1">{count}</span>
                <div className="w-full bg-hunter-green/60 rounded-t hover:bg-hunter-green transition-colors" style={{ height: `${h}px` }} />
                <span className="text-[8px] text-warm-charcoal/50 mt-1.5 -rotate-45 origin-top-left whitespace-nowrap">{day.slice(5)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'engagement', label: 'Engagement' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'dropoffs', label: 'Drop-offs' },
    { id: 'users', label: 'Users' },
    { id: 'broadcast', label: 'Broadcast' },
  ];

  const fetchNotifStatus = async () => {
    setLoadingNotifs(true);
    try {
      const res = await axios.get(`${API}/admin/notification-status`);
      setNotifStatus(res.data);
    } catch { toast.error('Failed to load notification status'); }
    finally { setLoadingNotifs(false); }
  };

  const [dropoffs, setDropoffs] = useState(null);
  const [loadingDropoffs, setLoadingDropoffs] = useState(false);

  const fetchDropoffs = async () => {
    setLoadingDropoffs(true);
    try {
      const res = await axios.get(`${API}/admin/dropoff-users`);
      setDropoffs(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoadingDropoffs(false); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email} and ALL their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/user/${userId}`);
      toast.success(`Deleted ${email}`);
      fetchNotifStatus();
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const [usageData, setUsageData] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [viewingUser, setViewingUser] = useState(null);

  const fetchUsage = async () => {
    setLoadingUsage(true);
    try { const res = await axios.get(`${API}/admin/user-usage`); setUsageData(res.data); }
    catch { toast.error('Failed to load usage data'); }
    finally { setLoadingUsage(false); }
  };

  const searchUsers = async (query) => {
    try {
      const res = await axios.get(`${API}/admin/users-search?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data);
    } catch { setSearchResults([]); }
  };

  const viewUserProfile = async (email) => {
    try {
      const res = await axios.get(`${API}/admin/user-profile/${encodeURIComponent(email)}`);
      setViewingUser(res.data);
    } catch { toast.error('User not found'); }
  };

  const sendTargeted = async (engagement) => {
    if (!broadcastSubject || !broadcastMessage) { toast.error('Fill in subject and message in Broadcast tab first'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/broadcast-targeted`, {
        subject: broadcastSubject, message: broadcastMessage,
        ...(engagement ? { engagement } : { emails: selectedUsers })
      });
      toast.success(`Sent to ${res.data.sent}/${res.data.target_count} users`);
    } catch { toast.error('Failed'); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-stone-50 to-stone-100">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-lg border-b border-stone-200/50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-stone-100">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Panel</h1>
          <button onClick={fetchAnalytics} className="ml-auto p-2 rounded-full hover:bg-stone-100">
            <RefreshCw className={`w-4 h-4 text-warm-charcoal/50 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {/* Tabs */}
        <div className="max-w-5xl mx-auto px-4 flex gap-1 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                activeTab === tab.id ? 'bg-hunter-green text-white' : 'text-warm-charcoal/50 hover:bg-stone-100'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        {loading && !analytics ? (
          <div className="flex justify-center py-20"><Loader className="w-8 h-8 animate-spin text-hunter-green" /></div>
        ) : analytics ? (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard icon={Users} label="Total Users" value={analytics.users.total} sub={`+${analytics.users.this_week} this week`} />
                  <StatCard icon={Users} label="Weekly Active" value={analytics.users.weekly_active} sub={`${analytics.users.today} signed up today`} />
                  <StatCard icon={Heart} label="Mood Entries" value={analytics.moods.total} sub={`+${analytics.moods.this_week} this week`} />
                  <StatCard icon={MessageCircle} label="Chat Messages" value={analytics.chats.total} sub={`+${analytics.chats.today} today`} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <StatCard icon={Bell} label="Notifications On" value={analytics.notifications_enabled} />
                  <StatCard icon={TrendingUp} label="Today's Check-ins" value={analytics.moods.today} />
                </div>
                <DailyChart data={analytics.daily_signups} title="Daily Signups (30 Days)" />
              </>
            )}


            {/* ENGAGEMENT TAB (includes user usage) */}
            {activeTab === 'engagement' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <StatCard icon={Heart} label="Avg. Check-ins/User" value={analytics.users.total > 0 ? (analytics.moods.total / analytics.users.total).toFixed(1) : '0'} />
                  <StatCard icon={MessageCircle} label="Avg. Chats/User" value={analytics.users.total > 0 ? (analytics.chats.total / analytics.users.total).toFixed(1) : '0'} />
                </div>
                <BarChart data={analytics.top_emotions_week} labelKey="emotion" valueKey="count" title="Top Emotions This Week" />
                <DailyChart data={analytics.daily_signups} title="User Growth Trend" />

                {/* Per-user usage */}
                {!usageData ? (
                  <Button onClick={fetchUsage} disabled={loadingUsage} className="w-full bg-hunter-green text-white">
                    {loadingUsage ? 'Loading...' : 'Load Per-User Usage'}
                  </Button>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <StatCard icon={TrendingUp} label="High" value={usageData.high_engagement} />
                      <StatCard icon={Users} label="Medium" value={usageData.medium_engagement} />
                      <StatCard icon={Users} label="Low" value={usageData.low_engagement} />
                    </div>
                    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                      <div className="p-2.5 border-b border-stone-100 bg-stone-50">
                        <div className="flex text-[9px] font-semibold text-warm-charcoal/60 uppercase tracking-wider">
                          <span className="flex-1">User</span>
                          <span className="w-12 text-center">Moods</span>
                          <span className="w-12 text-center">Chats</span>
                          <span className="w-12 text-center">Journal</span>
                          <span className="w-16 text-center">Features</span>
                          <span className="w-14 text-center">Level</span>
                        </div>
                      </div>
                      <div className="divide-y divide-stone-100 max-h-[300px] overflow-y-auto">
                        {usageData.users.map((u) => (
                          <div key={u.user_id} className="flex items-center px-2.5 py-2 text-xs">
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-medium text-warm-charcoal truncate">{u.email}</p>
                              <p className="text-[9px] text-warm-charcoal/40">{u.name}{u.country ? ` · ${u.country}` : ''}</p>
                            </div>
                            <span className="w-12 text-center text-[10px] text-warm-charcoal/60">{u.mood_entries}</span>
                            <span className="w-12 text-center text-[10px] text-warm-charcoal/60">{u.chat_messages}</span>
                            <span className="w-12 text-center text-[10px] text-warm-charcoal/60">{u.journal_entries}</span>
                            <span className="w-16 text-center text-[9px] text-warm-charcoal/50">{u.features_used.join(', ') || 'None'}</span>
                            <span className={`w-14 text-center text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                              u.engagement === 'high' ? 'bg-green-100 text-green-700' :
                              u.engagement === 'medium' ? 'bg-blue-100 text-blue-700' : 'bg-stone-100 text-stone-500'
                            }`}>{u.engagement}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button onClick={fetchUsage} variant="outline" className="w-full text-xs">
                      <RefreshCw className="w-3 h-3 mr-1" /> Refresh Usage
                    </Button>
                  </>
                )}
              </>
            )}


            {/* NOTIFICATIONS TAB */}
            {activeTab === 'notifications' && (
              <>
                {!notifStatus ? (
                  <div className="text-center py-8">
                    <Button onClick={fetchNotifStatus} disabled={loadingNotifs} className="bg-hunter-green text-white">
                      {loadingNotifs ? 'Loading...' : 'Load Notification Status'}
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-3 gap-3">
                      <StatCard icon={Bell} label="Active" value={notifStatus.active} />
                      <StatCard icon={Bell} label="Pending" value={notifStatus.never_notified} />
                      <StatCard icon={Bell} label="Opted Out" value={notifStatus.opted_out} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <StatCard icon={Send} label="Emails Sent" value={notifStatus.total_emails_sent} />
                      <StatCard icon={Heart} label="In-App Sent" value={notifStatus.total_inbox_sent} />
                      <StatCard icon={MessageCircle} label="Unread (In-App)" value={notifStatus.total_inbox_unread} />
                    </div>

                    {/* Channel filter */}
                    <div className="flex gap-1.5 mt-1">
                      {[
                        { id: 'all', label: 'All Channels' },
                        { id: 'email', label: 'Email' },
                        { id: 'inbox', label: 'In-App' },
                      ].map(f => (
                        <button
                          key={f.id}
                          onClick={() => setNotifFilter(f.id)}
                          className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors ${
                            notifFilter === f.id ? 'bg-hunter-green text-white' : 'bg-stone-100 text-warm-charcoal/50 hover:bg-stone-200'
                          }`}
                          style={{ touchAction: 'manipulation' }}
                        >{f.label}</button>
                      ))}
                    </div>

                    {/* User table */}
                    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                      <div className="p-3 border-b border-stone-100 bg-stone-50">
                        <div className="flex text-[10px] font-semibold text-warm-charcoal/60 uppercase tracking-wider">
                          <span className="w-20">ID</span>
                          <span className="flex-1">User</span>
                          <span className="w-16 text-center">Status</span>
                          {(notifFilter === 'all' || notifFilter === 'email') && <span className="w-14 text-center">Emails</span>}
                          {(notifFilter === 'all' || notifFilter === 'inbox') && <span className="w-14 text-center">In-App</span>}
                          <span className="w-20 text-center">Last Sent</span>
                          <span className="w-8"></span>
                        </div>
                      </div>
                      <div className="divide-y divide-stone-100 max-h-[400px] overflow-y-auto">
                        {notifStatus.users.map((u) => (
                          <div key={u.user_id} className="flex items-center p-2.5 text-xs">
                            <span className="w-20 text-[10px] font-mono text-warm-charcoal/40">{u.serial}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-warm-charcoal truncate text-[11px]">{u.email}</p>
                              <p className="text-[9px] text-warm-charcoal/40">{u.name} · {u.timezone}</p>
                              {u.issue && <p className="text-[9px] text-amber-600">{u.issue}</p>}
                            </div>
                            <span className={`w-16 text-center text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                              u.status === 'active' ? 'bg-green-100 text-green-700' :
                              u.status === 'never_notified' ? 'bg-amber-100 text-amber-700' :
                              'bg-stone-100 text-stone-500'
                            }`}>
                              {u.status === 'active' ? 'Active' : u.status === 'never_notified' ? 'Pending' : 'Out'}
                            </span>
                            {(notifFilter === 'all' || notifFilter === 'email') && (
                              <span className="w-14 text-center text-[10px] text-warm-charcoal/50">{u.emails_sent}</span>
                            )}
                            {(notifFilter === 'all' || notifFilter === 'inbox') && (
                              <span className="w-14 text-center">
                                {u.inbox_unread > 0 
                                  ? <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">{u.inbox_unread}/{u.inbox_total}</span>
                                  : <span className="text-[10px] text-warm-charcoal/30">{u.inbox_total}</span>
                                }
                              </span>
                            )}
                            <span className="w-20 text-center text-[9px] text-warm-charcoal/40">
                              {u.last_notified === 'never' ? 'Never' : new Date(u.last_notified).toLocaleDateString()}
                            </span>
                            <span className="w-8 text-center">
                              <button onClick={() => deleteUser(u.user_id, u.email)} className="p-1 rounded hover:bg-red-50 text-warm-charcoal/20 hover:text-red-500" title={`Delete ${u.email}`} style={{ touchAction: 'manipulation' }}>
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Button onClick={fetchNotifStatus} variant="outline" className="w-full text-xs mt-2">
                      <RefreshCw className="w-3 h-3 mr-1" /> Refresh Status
                    </Button>
                  </>
                )}
              </>
            )}




            {/* USERS TAB — search and view preferences */}
            {activeTab === 'users' && (
              <>
                <div className="bg-white rounded-xl p-4 border border-stone-200 space-y-3">
                  <h3 className="text-xs font-semibold text-warm-charcoal/60 uppercase tracking-wider">Search Users</h3>
                  <div className="flex gap-2">
                    <Input
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchUsers(userSearch)}
                      placeholder="Search by name or email..."
                      className="text-sm flex-1"
                    />
                    <Button onClick={() => searchUsers(userSearch)} className="bg-hunter-green text-white px-4">
                      Search
                    </Button>
                  </div>
                  {!userSearch && searchResults.length === 0 && (
                    <button onClick={() => searchUsers('')} className="text-xs text-hunter-green font-medium">Show all users</button>
                  )}
                </div>

                {searchResults.length > 0 && (
                  <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <div className="divide-y divide-stone-100 max-h-[300px] overflow-y-auto">
                      {searchResults.map((u, i) => (
                        <button
                          key={i}
                          onClick={() => viewUserProfile(u.email)}
                          className="w-full text-left px-3 py-2.5 hover:bg-stone-50 flex items-center justify-between"
                          style={{ touchAction: 'manipulation' }}
                        >
                          <div>
                            <p className="text-xs font-medium text-warm-charcoal">{u.email}</p>
                            <p className="text-[9px] text-warm-charcoal/40">{u.name} {u.surname || ''}{u.country ? ` · ${u.country}` : ''}</p>
                          </div>
                          <span className="text-[10px] text-hunter-green">View</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* User profile detail */}
                {viewingUser && (
                  <div className="bg-white rounded-xl p-4 border-2 border-hunter-green/20 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-warm-charcoal" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {viewingUser.name} {viewingUser.surname || ''}
                      </h3>
                      <button onClick={() => setViewingUser(null)} className="text-xs text-warm-charcoal/40">Close</button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {[
                        ['Email', viewingUser.email],
                        ['Gender', viewingUser.gender || '—'],
                        ['Country', viewingUser.country || '—'],
                        ['City', viewingUser.city || '—'],
                        ['Area', viewingUser.area || '—'],
                        ['Language', viewingUser.language || '—'],
                        ['Email Verified', viewingUser.email_verified ? 'Yes' : 'No'],
                        ['Registered', viewingUser.created_at?.substring(0, 10) || '—'],
                      ].map(([label, value]) => (
                        <div key={label} className="p-2 bg-stone-50 rounded-lg">
                          <p className="text-[9px] text-warm-charcoal/50 uppercase tracking-wider">{label}</p>
                          <p className="text-xs font-medium text-warm-charcoal mt-0.5">{value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Activity & Engagement */}
                    {viewingUser.activity && (
                      <div className="pt-2 border-t border-stone-200 space-y-2">
                        <h4 className="text-[10px] font-semibold text-warm-charcoal/60 uppercase tracking-wider">Activity & Engagement</h4>
                        <div className="grid grid-cols-4 gap-2">
                          <div className="p-2 bg-stone-50 rounded-lg text-center">
                            <p className="text-lg font-bold text-hunter-green">{viewingUser.activity.mood_entries}</p>
                            <p className="text-[9px] text-warm-charcoal/50">Moods</p>
                          </div>
                          <div className="p-2 bg-stone-50 rounded-lg text-center">
                            <p className="text-lg font-bold text-hunter-green">{viewingUser.activity.chat_messages}</p>
                            <p className="text-[9px] text-warm-charcoal/50">Chats</p>
                          </div>
                          <div className="p-2 bg-stone-50 rounded-lg text-center">
                            <p className="text-lg font-bold text-hunter-green">{viewingUser.activity.journal_entries}</p>
                            <p className="text-[9px] text-warm-charcoal/50">Journals</p>
                          </div>
                          <div className="p-2 bg-stone-50 rounded-lg text-center">
                            <p className={`text-sm font-bold ${viewingUser.activity.engagement === 'high' ? 'text-green-600' : viewingUser.activity.engagement === 'medium' ? 'text-blue-600' : 'text-stone-400'}`}>
                              {viewingUser.activity.engagement.toUpperCase()}
                            </p>
                            <p className="text-[9px] text-warm-charcoal/50">Level</p>
                          </div>
                        </div>
                        {viewingUser.activity.last_active && (
                          <p className="text-[10px] text-warm-charcoal/50">Last active: {viewingUser.activity.last_active.substring(0, 10)}</p>
                        )}

                        {/* Recent Mood History */}
                        {viewingUser.activity.recent_moods?.length > 0 && (
                          <div>
                            <p className="text-[9px] text-warm-charcoal/50 mb-1">Recent Emotions</p>
                            <div className="space-y-0.5">
                              {viewingUser.activity.recent_moods.map((m, i) => (
                                <div key={i} className="flex items-center justify-between text-[10px] py-0.5">
                                  <span className="text-warm-charcoal font-medium">{m.feeling}</span>
                                  <span className="text-warm-charcoal/40">{m.date}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recent Chat Activity */}
                        {viewingUser.activity.recent_chats?.length > 0 && (
                          <div>
                            <p className="text-[9px] text-warm-charcoal/50 mb-1">Recent Chats</p>
                            <div className="space-y-0.5">
                              {viewingUser.activity.recent_chats.map((c, i) => (
                                <div key={i} className="flex items-center justify-between text-[10px] py-0.5">
                                  <span className="text-warm-charcoal truncate flex-1 mr-2">"{c.preview}..."</span>
                                  <span className="text-warm-charcoal/40 flex-shrink-0">{c.date}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Preferences */}
                    {(viewingUser.music_preferences?.length > 0 || viewingUser.food_preferences?.length > 0 || viewingUser.wellness_goals?.length > 0) && (
                      <div className="space-y-2 pt-2 border-t border-stone-200">
                        <h4 className="text-[10px] font-semibold text-warm-charcoal/60 uppercase tracking-wider">Onboarding Preferences</h4>
                        {viewingUser.music_preferences?.length > 0 && (
                          <div>
                            <p className="text-[9px] text-warm-charcoal/50">Music</p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {viewingUser.music_preferences.map(p => (
                                <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-hunter-green/10 text-hunter-green">{p}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {viewingUser.food_preferences?.length > 0 && (
                          <div>
                            <p className="text-[9px] text-warm-charcoal/50">Food</p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {viewingUser.food_preferences.map(p => (
                                <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{p}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {viewingUser.wellness_goals?.length > 0 && (
                          <div>
                            <p className="text-[9px] text-warm-charcoal/50">Wellness Goals</p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {viewingUser.wellness_goals.map(g => (
                                <span key={g} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{g}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {viewingUser.favorite_artists?.length > 0 && (
                      <div>
                        <p className="text-[9px] text-warm-charcoal/50">Favorite Artists</p>
                        <p className="text-xs text-warm-charcoal mt-0.5">{viewingUser.favorite_artists.join(', ')}</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}


            {/* DROPOFFS TAB */}
            {activeTab === 'dropoffs' && (
              <>
                {!dropoffs ? (
                  <div className="text-center py-8">
                    <p className="text-xs text-warm-charcoal/50 mb-3">Find users who registered but never completed onboarding or recorded an emotion.</p>
                    <Button onClick={fetchDropoffs} disabled={loadingDropoffs} className="bg-hunter-green text-white">
                      {loadingDropoffs ? 'Analyzing...' : 'Analyze Drop-offs'}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <StatCard icon={Users} label="Completed Users" value={dropoffs.completed} />
                      <StatCard icon={Users} label="Dropped Off" value={dropoffs.dropoffs} />
                    </div>

                    {dropoffs.dropoff_users.length > 0 && (
                      <>
                        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                          <div className="p-3 border-b border-stone-100 bg-stone-50">
                            <p className="text-[10px] font-semibold text-warm-charcoal/60 uppercase tracking-wider">
                              Users Who Dropped Off ({dropoffs.dropoff_users.length})
                            </p>
                          </div>
                          <div className="divide-y divide-stone-100 max-h-[300px] overflow-y-auto">
                            {dropoffs.dropoff_users.map((u, i) => (
                              <div key={i} className="px-3 py-2 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-warm-charcoal">{u.email}</span>
                                  <span className="text-[9px] text-warm-charcoal/40">{u.registered?.substring(0, 10)}</span>
                                </div>
                                <div className="flex gap-1 mt-1 flex-wrap">
                                  {u.drop_reason?.map((r, j) => (
                                    <span key={j} className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{r}</span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Email list for copy */}
                        <div className="bg-white rounded-xl p-4 border border-stone-200">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-warm-charcoal/60">Email List (copy for re-engagement)</p>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(dropoffs.dropoff_emails.join(', '));
                                toast.success('Email list copied!');
                              }}
                              className="text-[10px] px-2 py-1 rounded bg-hunter-green/10 text-hunter-green font-medium"
                            >Copy All</button>
                          </div>
                          <p className="text-[10px] text-warm-charcoal/40 break-all leading-relaxed">
                            {dropoffs.dropoff_emails.join(', ')}
                          </p>
                        </div>

                        {/* Pre-drafted re-engagement */}
                        <div className="bg-white rounded-xl p-4 border border-stone-200">
                          <p className="text-xs font-semibold text-warm-charcoal/60 mb-2">Draft Re-engagement Message</p>
                          <button
                            onClick={() => {
                              setActiveTab('broadcast');
                              setBroadcastSubject('We Fixed It! Come Complete Your MindMyMojo Journey');
                              setBroadcastMessage(`We noticed you started your emotional wellness journey with MindMyMojo but couldn't complete the sign-up process. We're sorry about that — we had a technical issue with our email verification system that has now been fully resolved.\n\nWe'd love for you to give us another try. Here's what awaits you:\n\n• Record and understand your emotions with 18 scientifically-backed categories\n• Chat with Mojo — your personal AI wellness companion, available 24/7\n• Discover how people around the world are feeling, in real-time\n• Get mood-based music recommendations in 6 languages\n• Journal your thoughts for deeper self-understanding\n\nTo stay connected and never miss a check-in reminder, bookmark MindMyMojo on your device:\n\niPhone/iPad: Open mindmymojo.com in Safari > Tap Share > "Add to Home Screen"\nAndroid: Open mindmymojo.com in Chrome > Tap menu (three dots) > "Add to Home Screen"\nDesktop: Visit mindmymojo.com > Press Ctrl+D (Cmd+D on Mac)\n\nYour emotional wellness matters to us. Come back and complete your profile — it takes less than 2 minutes.`);
                            }}
                            className="w-full text-left p-3 rounded-lg bg-hunter-green/5 border border-hunter-green/20 hover:bg-hunter-green/10 transition-colors"
                          >
                            <p className="text-xs font-medium text-hunter-green">Send Re-engagement to Drop-offs</p>
                            <p className="text-[10px] text-warm-charcoal/50 mt-0.5">Opens broadcast with pre-drafted "Come back" message</p>
                          </button>
                        </div>

                        <Button onClick={fetchDropoffs} variant="outline" className="w-full text-xs">
                          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                        </Button>
                      </>
                    )}
                  </>
                )}
              </>
            )}


            {/* BROADCAST TAB */}
            {activeTab === 'broadcast' && (
              <div className="space-y-4">
                {/* Templates */}
                <div className="bg-white rounded-xl p-4 border border-stone-200">
                  <h3 className="text-xs font-semibold text-warm-charcoal/60 uppercase tracking-wider mb-3">Quick Templates</h3>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { 
                        label: 'Feature Announcement', 
                        subject: 'Discover Everything MindMyMojo Has to Offer',
                        message: `Did you know MindMyMojo is packed with features to support your emotional wellness journey? Here's what you can explore:\n\n🤖 Chat & Voice with Mojo — Your AI wellness companion is available 24/7. Type or speak — Mojo listens with empathy, helps you process feelings, and responds in natural, calming voices. Choose the voice that feels right for you.\n\n🌍 Community Insights — See how people around the world are feeling right now. Filter by region and country. You're never alone in what you feel.\n\n🎯 Record Your Emotions — Track how you feel with our 18 scientifically-backed emotion categories from the Hoffman Process\n\n🎵 Mood-Based Music — Get personalized song recommendations across 6 languages, filterable by era, genre, and artist\n\n📔 Reflective Journal — Write your thoughts and pair them with your mood for deeper self-understanding\n\n🌐 Available in 6 Languages — English, Hindi, Spanish, French, German, and Arabic. Switch anytime from the menu.\n\n📊 Mood History & PDF Reports — Track your patterns over time and export detailed reports to share with your therapist or coach\n\n🔔 Smart Reminders — Get gentle check-in nudges twice a day at your local time\n\n📱 Share Your Mood — Beautiful visual cards to share your emotional state on WhatsApp, Instagram, X, and Facebook\n\nTake a moment today to explore a feature you haven't tried yet. Your emotional wellness journey is unique — and MindMyMojo is here for every step.`
                      },
                      {
                        label: 'Re-engagement',
                        subject: 'We Miss You! Stay Connected with MindMyMojo',
                        message: `It's been a while since your last check-in, and we wanted you to know — MindMyMojo is still here for you.\n\nEmotional wellness isn't about being perfect. It's about showing up for yourself, even on the hard days. A quick 30-second check-in can make a real difference.\n\nAre you missing our check-in reminders? Make sure you've bookmarked MindMyMojo so you never miss a notification:\n\niPhone/iPad: Open mindmymojo.com in Safari > Tap the Share button (square with arrow) at the bottom > Tap "Add to Home Screen"\n\nAndroid: Open mindmymojo.com in Chrome > Tap the menu (three dots) > Tap "Add to Home Screen"\n\nDesktop: Visit mindmymojo.com > Press Ctrl+D (or Cmd+D on Mac) to bookmark\n\nOnce bookmarked, you'll receive our gentle check-in nudges and never miss an update. Your emotional wellness journey matters — and we're here for every step.\n\nCome back and tell us how you're feeling. Mojo is waiting to listen.`
                      },
                      {
                        label: 'Community Milestone',
                        subject: 'Our Community Is Growing!',
                        message: `We're thrilled to share that our MindMyMojo community is growing every day. People from around the world are joining the movement towards emotional self-awareness.\n\nCheck out Community Insights to see how others are feeling right now. Remember — understanding your emotions is a superpower, and you're already on that path.\n\nThank you for being part of this journey.`
                      },
                      {
                        label: 'Refer a Friend',
                        subject: 'Share the Gift of Emotional Wellness',
                        message: `You know how good it feels to check in with your emotions? Imagine giving that gift to someone you care about.\n\nShare MindMyMojo with a friend or family member — it takes just a tap:\n\nWhatsApp: Send them this link — https://mindmymojo.com\nInstagram: Share a screenshot of your mood card in your Stories and tag @mindmymojo\nFacebook: Post about your wellness journey and include the link https://mindmymojo.com\n\nEmotional wellness is better together. When the people around you are more self-aware, your relationships grow deeper and more meaningful.\n\nThank you for spreading the word. Every share helps someone take the first step towards understanding themselves better.`
                      }
                    ].map((t, i) => (
                      <button
                        key={i}
                        onClick={() => { setBroadcastSubject(t.subject); setBroadcastMessage(t.message); }}
                        className="text-left p-3 rounded-lg bg-stone-50 hover:bg-hunter-green/5 border border-stone-200 hover:border-hunter-green/30 transition-colors"
                        style={{ touchAction: 'manipulation' }}
                      >
                        <p className="text-xs font-semibold text-warm-charcoal">{t.label}</p>
                        <p className="text-[10px] text-warm-charcoal/50 mt-0.5 line-clamp-1">{t.subject}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Compose */}
                <div className="bg-white rounded-xl p-5 border border-stone-200 space-y-4">
                  <div>
                    <h2 className="text-sm font-semibold text-warm-charcoal mb-1">Compose Broadcast</h2>
                    <p className="text-xs text-warm-charcoal/50">Send to all or select specific users below.</p>
                  </div>
                <Input
                  value={broadcastSubject}
                  onChange={(e) => setBroadcastSubject(e.target.value)}
                  placeholder="Subject"
                  className="text-sm"
                />
                <Textarea
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  placeholder="Write your message..."
                  rows={4}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={sendBroadcast}
                    disabled={sending || !broadcastMessage.trim()}
                    className="flex-1 bg-hunter-green text-white hover:bg-hunter-green/90"
                  >
                    {sending ? <Loader className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send to All
                  </Button>
                  <Button
                    onClick={() => sendTargeted()}
                    disabled={sending || !broadcastMessage.trim() || selectedUsers.length === 0}
                    variant="outline"
                    className="flex-1 border-hunter-green text-hunter-green"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send to Selected ({selectedUsers.length})
                  </Button>
                </div>
                </div>

                {/* User selection */}
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                  <div className="p-3 border-b border-stone-100 bg-stone-50 flex items-center justify-between">
                    <p className="text-[10px] font-semibold text-warm-charcoal/60 uppercase tracking-wider">Select Recipients</p>
                    <div className="flex gap-2">
                      <button onClick={() => {
                        if (!usageData) { fetchUsage(); return; }
                        setSelectedUsers(selectedUsers.length === usageData.users.length ? [] : usageData.users.map(u => u.email));
                      }} className="text-[10px] text-hunter-green font-medium">
                        {selectedUsers.length > 0 ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                  </div>
                  {!usageData ? (
                    <div className="p-4 text-center">
                      <button onClick={fetchUsage} disabled={loadingUsage} className="text-xs text-hunter-green font-medium">
                        {loadingUsage ? 'Loading...' : 'Load Users'}
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-stone-100 max-h-[250px] overflow-y-auto">
                      {usageData.users.map((u) => (
                        <label key={u.user_id} className="flex items-center px-3 py-2 hover:bg-stone-50 cursor-pointer" style={{ touchAction: 'manipulation' }}>
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(u.email)}
                            onChange={(e) => {
                              setSelectedUsers(prev => e.target.checked
                                ? [...prev, u.email]
                                : prev.filter(em => em !== u.email)
                              );
                            }}
                            className="w-4 h-4 rounded border-stone-300 text-hunter-green focus:ring-hunter-green mr-3"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-medium text-warm-charcoal truncate">{u.email}</p>
                            <p className="text-[9px] text-warm-charcoal/40">{u.name}{u.engagement ? ` · ${u.engagement}` : ''}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-center text-warm-charcoal/50 py-10">Failed to load analytics. Admin access required.</p>
        )}

        {analytics && (
          <p className="text-[10px] text-warm-charcoal/30 text-center">
            Generated: {new Date(analytics.generated_at).toLocaleString()}
          </p>
        )}
      </main>
    </div>
  );
}
